import { useTranslation } from "@opendash/core";
import { _User } from "@opendash/plugin-parse/dist/types-generated";
import { Button, Form, InputNumber, Modal, TimePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import Parse from "parse";
import React, { useEffect, useState } from "react";
import { Stop, Stop_Times, Trip } from "../js/parsegenerated";

interface StopTimesDataType {
  arrival_time: Dayjs;
  departure_time: Dayjs;
  stop_sequence: number;
}

export const AddTimesModal = (props: {
  open: boolean;
  stopid: string;
  tripid: string;
  onOk: Function;
  onCancel: Function;
  stopTime?: Stop_Times;
}) => {
  const t = useTranslation();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const save = () => {
    form.validateFields().then(async (values: StopTimesDataType) => {
      const stop = await new Parse.Query(Stop).get(props.stopid);
      const trip = await new Parse.Query(Trip).get(props.tripid);
      const x = new Stop_Times();
      try {
        // save existing
        if (props.stopTime) {
          const laterStops = await new Parse.Query(Stop_Times)
            .greaterThan("stop_sequence", props.stopTime.stop_sequence)
            .equalTo("trip_id", trip)
            .find();

          const savedStops = laterStops.map((stop) => {
            stop.decrement("stop_sequence");
            return stop.save();
          });

          await Promise.all(savedStops);

          const savedStopTime = await props.stopTime.save({
            stop_sequence: values.stop_sequence,
            arrival_time: values.arrival_time.toDate(),
            departure_time: values.departure_time.toDate(),
          });
        }
        let key = props.stopTime?.id;

        if (!props.stopTime) {
          const newStopTime = await x.save({
            stop_id: stop,
            trip_id: trip,
            stop_sequence: values.stop_sequence,
            arrival_time: values.arrival_time.toDate(),
            departure_time: values.departure_time.toDate(),
            tenant: (Parse.User.current() as _User).get("tenant"),
          });
          key = newStopTime.id;
        }
        const laterStops = await new Parse.Query(Stop_Times)
          .greaterThanOrEqualTo("stop_sequence", values.stop_sequence)
          .equalTo("trip_id", trip)
          .find();
        for (const stop of laterStops) {
          if (stop.id !== key) {
            stop.increment("stop_sequence");
            stop.save();
          }
        }
        props.onOk();
        form.resetFields();
      } catch (error) {
        throw new Error(t("gtfs:global.message.saveerror"));
      }
    });
  };

  useEffect(() => {
    if (props.stopTime) {
      form.setFieldsValue({
        stop_sequence: props.stopTime.get("stop_sequence"),
        arrival_time: dayjs(props.stopTime.get("arrival_time")),
        departure_time: dayjs(props.stopTime.get("departure_time")),
      });
    }
  }, [props.stopTime]);

  return (
    <Modal
      title={t("gtfs:route.create.modal.title")}
      open={props.open}
      onOk={save}
      onCancel={() => {
        props.onCancel();
        form.resetFields();
      }}
      width={"80%"}
      getContainer={false}
      footer={[
        <Button
          key="back"
          onClick={() => {
            props.onCancel();
            form.resetFields();
          }}
        >
          {t("gtfs:global.button.cancel_label")}
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={save}>
          {t("gtfs:global.button.save_label")}
        </Button>,
      ]}
    >
      <Form autoComplete="off" form={form} layout="vertical">
        <Form.Item
          name="arrival_time"
          label={t("gtfs:stop_times.create.modal.form.arrival_time.label")}
          help={t("gtfs:stop_times.create.modal.form.arrival_time.help")}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <TimePicker showNow={false} format="HH:mm" />
        </Form.Item>
        <Form.Item
          name="departure_time"
          label={t("gtfs:stop_times.create.modal.form.departure_time.label")}
          help={t("gtfs:stop_times.create.modal.form.departure_time.help")}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <TimePicker showNow={false} format="HH:mm" />
        </Form.Item>
        <Form.Item
          name="stop_sequence"
          label={t("gtfs:stop_times.create.modal.form.stop_sequence.label")}
          help={t("gtfs:stop_times.create.modal.form.stop_sequence.help")}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <InputNumber />
        </Form.Item>
      </Form>
    </Modal>
  );
};
